import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  getTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  getTeamHierarchy
} from '../services/teamService';

const TeamContext = createContext();

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

export const TeamProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [userRole, setUserRole] = useState('manager'); // 'manager', 'director', 'avp'
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load teams when user changes
  useEffect(() => {
    if (currentUser) {
      loadTeams();
      loadHierarchy();
    } else {
      setTeams([]);
      setCurrentTeam(null);
      setHierarchy(null);
    }
  }, [currentUser]);

  const loadTeams = async () => {
    setLoading(true);
    try {
      const data = await getTeams();
      setTeams(data || []);
      
      // Auto-select first team if none selected
      if (data && data.length > 0 && !currentTeam) {
        setCurrentTeam(data[0]);
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      // Set empty array on error to prevent crashes
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHierarchy = async () => {
    try {
      const data = await getTeamHierarchy();
      setHierarchy(data || null);
    } catch (error) {
      console.error('Error loading hierarchy:', error);
      setHierarchy(null);
    }
  };

  const addTeam = async (teamData) => {
    try {
      let newTeam;
      
      // Check if this is a setup completion
      if (teamData.setupComplete) {
        const { completeTeamSetup } = await import('../services/teamService');
        newTeam = await completeTeamSetup(teamData);
      } else {
        newTeam = await createTeam({
          ...teamData,
          userId: currentUser.uid,
          createdAt: new Date().toISOString()
        });
      }
      
      setTeams(prev => [...prev, newTeam]);
      await loadHierarchy();
      
      return { success: true, team: newTeam };
    } catch (error) {
      console.error('Error creating team:', error);
      return { success: false, error: error.message };
    }
  };

  const modifyTeam = async (teamId, updates) => {
    try {
      const updatedTeam = await updateTeam(teamId, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
      
      setTeams(prev => prev.map(t => t.id === teamId ? updatedTeam : t));
      
      if (currentTeam?.id === teamId) {
        setCurrentTeam(updatedTeam);
      }
      
      await loadHierarchy();
      
      return { success: true, team: updatedTeam };
    } catch (error) {
      console.error('Error updating team:', error);
      return { success: false, error: error.message };
    }
  };

  const removeTeam = async (teamId) => {
    try {
      await deleteTeam(teamId);
      setTeams(prev => prev.filter(t => t.id !== teamId));
      
      if (currentTeam?.id === teamId) {
        setCurrentTeam(teams.find(t => t.id !== teamId) || null);
      }
      
      await loadHierarchy();
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting team:', error);
      return { success: false, error: error.message };
    }
  };

  const switchTeam = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (team) {
      setCurrentTeam(team);
    }
  };

  const switchRole = (role) => {
    if (['manager', 'director', 'avp'].includes(role)) {
      setUserRole(role);
    }
  };

  const value = {
    teams,
    currentTeam,
    userRole,
    hierarchy,
    loading,
    addTeam,
    modifyTeam,
    removeTeam,
    switchTeam,
    switchRole,
    loadTeams,
    loadHierarchy
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};
